<template>
  <Teleport to="body">
    <div class="upload-mask" @click.self="handleClose">
      <div
          class="upload-modal"
          :class="{ 'drag-over': isDragOver }"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
      >
        <div class="upload-inner">
          <div class="upload-content">
            <button
                class="upload-button"
                :class="{ loading: uploading }"
                :disabled="uploading"
                @click="handleUploadClick"
            >
              <div class="upload-button-inner">
                <div class="upload-icon-wrapper" v-if="!uploading">
                  <img :src="uploadIcon" style="width: 14px;height: 14px;">
                </div>
                <span class="upload-button-text">
                  {{ uploading ? '上传中..' : '上传图片' }}
                </span>
              </div>
              <span v-if="uploading" class="flash-sheen" aria-hidden="true"></span>
            </button>
            <div class="upload-hint">
              <div class="upload-hint-title">建议上传图片能清晰展示商品，以保障生成效果</div>
              <div class="upload-hint-sub-title">拖放或上传照片（JPG、PNG，最大4MB）</div>
            </div>
          </div>
          <div class="example-images-section-container">
            <div class="example-images-section">
              <div class="example-label">示例图</div>
              <div class="example-images">
                <div
                    v-for="(img, index) in exampleImages"
                    :key="index"
                    class="example-image-wrapper"
                    @mouseenter="handleImageHover(index)"
                    @mouseleave="handleImageLeave"
                >
                  <img
                      :src="img.src"
                      :alt="img.alt"
                      class="example-image"
                  />
                  <div
                      v-if="hoveredIndex === index"
                      class="preview-image"
                  >
                    <img
                        :src="img.src"
                        :alt="img.alt"
                        class="preview-image-content"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <input
            ref="fileInput"
            type="file"
            accept=".jpg,.jpeg,.png,.mp4"
            style="display: none"
            @change="handleFileChange"
        />
      </div>
    </div>
    <QuotaTipModal
        :visible="showQuotaModal"
        @close="showQuotaModal = false"
    />
  </Teleport>
</template>

<script setup>
/* global defineEmits */
import {ref} from 'vue';
import {useRouter} from 'vue-router';
import {SUCCESS_CODE, uploadClothesImage, createCvTask} from '@/api/api';
import Message from '@/utils/message';
import exampleImage1 from '@/assets/example_image_1.jpg';
import exampleImage2 from '@/assets/example_image_2.jpg';
import exampleImage3 from '@/assets/example_image_3.jpg';
import QuotaTipModal from '@/components/QuotaTipModal.vue';

const emit = defineEmits(['close', 'upload']);
import uploadIcon from '@/assets/upload_icon.png'
const fileInput = ref(null);
const uploading = ref(false);
const router = useRouter();
const hoveredIndex = ref(null);
const isDragOver = ref(false);
const showQuotaModal = ref(false);

const exampleImages = [
  {src: exampleImage1, alt: '示例图1'},
  {src: exampleImage2, alt: '示例图2'},
  {src: exampleImage3, alt: '示例图3'},
];

const handleImageHover = (index) => {
  hoveredIndex.value = index;
};

const handleImageLeave = () => {
  hoveredIndex.value = null;
};

const handleClose = () => {
  emit('close');
};

const handleUploadClick = () => {
  if (uploading.value) return;
  fileInput.value?.click();
};

const validateFile = (file) => {
  if (!file) return false;
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'video/mp4'];
  const validExtensions = ['.jpg', '.jpeg', '.png', '.mp4'];
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

  if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
    Message.error('不支持的文件格式，请上传 JPG、PNG 或 MP4 格式的文件');
    return false;
  }

  const maxSize = 4 * 1024 * 1024; // 20MB
  if (file.size > maxSize) {
    Message.error('文件大小不能超过 20MB');
    return false;
  }

  return true;
};

const processFile = async (file) => {
  if (!file || uploading.value) return;
  if (!validateFile(file)) return;

  try {
    uploading.value = true;
    const formData = new FormData();
    formData.append('image', file);

    const uploadRes = await uploadClothesImage(formData);

    if (uploadRes?.code !== SUCCESS_CODE || !uploadRes?.data) {
      Message.error(uploadRes?.msg || '图片上传失败');
      return;
    }

    const data = uploadRes.data;
    const taskPayload = {
      imageUrl: data.url,
      matting: data.matting,
      target:data.target,
      clarity: 'HD',
      orientation: 'portrait',
      duration: 25,
      narration: '',
      attributes: {},
    };

    const taskRes = await createCvTask(taskPayload);

    if (taskRes?.code === 50029) {
      showQuotaModal.value = true;
      return;
    }
    if (taskRes?.code !== SUCCESS_CODE || taskRes?.success !== true) {
      Message.error(taskRes?.msg || '任务创建失败');
      return;
    }

    Message.success(taskRes?.msg || '任务创建成功');
    emit('upload', file);
    emit('close');
    router.push({
      name: 'Chat',
      query: {taskId: taskRes?.data?.id || ''},
    });
  } catch (error) {
    console.error('Upload flow failed:', error);
    Message.error('上传或创建任务失败，请重试');
  } finally {
    uploading.value = false;
  }
};

const handleFileChange = async (event) => {
  const file = event.target.files?.[0];
  await processFile(file);
  if (event?.target) {
    event.target.value = '';
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  if (!uploading.value) {
    isDragOver.value = true;
  }
};

const handleDragLeave = (event) => {
  event.preventDefault();
  // 只有当离开整个拖拽区域时才取消高亮
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = false;
  }
};

const handleDrop = async (event) => {
  event.preventDefault();
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return;

  const file = files[0];
  await processFile(file);
};
</script>

<style scoped>
.upload-mask {
  position: fixed;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.86));
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.upload-modal {
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-sizing: border-box;
  backdrop-filter: blur(21.63px);
  background: linear-gradient(
      64.41deg,
      rgba(250, 216, 124, 0.2) 0%,
      rgba(250, 216, 124, 0) 100%
  );
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.upload-modal.drag-over {
  border-color: rgba(250, 216, 124, 0.5);
  background: linear-gradient(
      64.41deg,
      rgba(250, 216, 124, 0.35) 0%,
      rgba(250, 216, 124, 0.15) 100%
  );
}

.upload-inner {
  border-radius: 16px;
  border: 0.95px solid rgba(255, 255, 255, 0.08);
  box-sizing: border-box;
  background: rgba(27, 24, 19, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 50px 180px;
  position: relative;
  overflow: visible;
}

.example-images-section-container {
  width: 300px;
  margin: 0 auto;
}

.upload-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  flex-shrink: 0;
}

.upload-button {
  width: auto;
  padding: 12px 20px;
  border-radius: 8px;
  border: 1px solid rgba(221, 180, 99, 1);
  background: linear-gradient(
      39.27deg,
      rgba(188, 130, 58, 1) 0%,
      rgba(247, 207, 115, 1) 51.44%,
      rgba(227, 156, 24, 1) 100%
  );
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  transition: transform 0.1s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;
}

.upload-button:disabled {
  cursor: not-allowed;
  filter: grayscale(0.08);
  opacity: 0.85;
  box-shadow: none;
}

.upload-button.loading {
  position: relative;
}

.upload-button.loading .upload-button-inner {
  position: relative;
  z-index: 2;
  opacity: 0.95;
}

.flash-sheen {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.35) 40%,
      rgba(255, 255, 255, 0) 80%
  );
  transform: translateX(-120%);
  animation: flash-run 1.2s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}

.upload-button.loading::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  z-index: 0;
}

.upload-button.loading .upload-button-text {
  color: #120f0d;
  position: relative;
  z-index: 2;
}

@keyframes flash-run {
  0% {
    transform: translateX(-120%);
  }
  50% {
    transform: translateX(10%);
  }
  100% {
    transform: translateX(120%);
  }
}

.upload-button:hover {
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.32);
}

.upload-button:active {
  transform: translateY(1px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.26);
}

.upload-button-inner {
  width: 92px;
  height: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.upload-icon-wrapper {
  width: 20px;
  height: 20px;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.upload-icon {
  width: 18.33px;
  height: 16.67px;
  margin-left: 0.83px;
  color: rgba(18, 16, 13, 1);
}

.upload-button-text {
  margin-left: 8px;
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  white-space: nowrap;
  text-align: center;
  color: rgba(18, 16, 13, 1);
  line-height: normal;
  font-weight: 500;
  flex-shrink: 0;
}

.upload-hint {
  margin-top: 24px;
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  text-align: center;
  margin-bottom: 30px;
  width: 100%;
  color: rgba(231, 228, 223, 1);
  line-height: normal;
  font-weight: 400;
  flex-shrink: 0;
}

.upload-hint-sub-title {
  color: #807666;
  text-align: center;
  font-family: "PingFang SC";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
}

.upload-hint-title {
  color: #E7E4DF;
  text-align: center;
  margin-bottom: 10px;
  font-family: "PingFang SC";
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
}


.example-images-section {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  position: relative;
  overflow: visible;
  padding: 8px 6px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.example-label {
  writing-mode: vertical-rl;
  text-orientation: upright;
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  color: rgba(231, 228, 223, 0.8);
  line-height: normal;
  font-weight: 400;
  letter-spacing: 2px;
  padding: 8px 0;
  flex-shrink: 0;
}

.example-images {
  display: flex;
  flex-direction: row;
  gap: 12px;
  flex: 1;
  justify-content: flex-start;
  align-items: center;
}

.example-image-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: visible;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.example-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

.preview-image {
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  pointer-events: none;
  animation: previewFadeIn 0.3s ease;
}

.preview-image-content {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: block;
}

@keyframes previewFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(8px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}
</style>
